import { useEffect, useState, type Dispatch, type SetStateAction, useMemo } from 'react';
import { useSessionStorage } from 'usehooks-ts';
import { type FilingCabinet } from '@/Models/FilingCabinet/FilingCabinet';

const createNewCabinet = (): FilingCabinet => {
    return {
        files: [],
    };
};

const parseFilingCabinetBackend = (backend: string): FilingCabinet => {
    if (!backend) {
        return createNewCabinet();
    }

    try {
        const object = JSON.parse(backend);
        return object as FilingCabinet;
    }
    catch {
        console.warn(`Invalid JSON: ${backend}`);
        return createNewCabinet();
    }
};

type useFilingCabinetBackendInterface = [FilingCabinet, Dispatch<SetStateAction<FilingCabinet>>];

const useFilingCabinetBackend = (): useFilingCabinetBackendInterface => {
    const [filingCabinetLocalStorage, setFilingCabinetLocalStorage] = useSessionStorage<string>('RisOnWeb-filingCabinet', '');
    const [filingCabinet, setFilingCabinet] = useState<FilingCabinet>(() => filingCabinetLocalStorage ? parseFilingCabinetBackend(filingCabinetLocalStorage) : createNewCabinet());

    useEffect(() => {
        const newValue = filingCabinet ?? createNewCabinet();
        const newValueJSON = JSON.stringify(newValue);

        if (newValueJSON !== filingCabinetLocalStorage) {
            setFilingCabinetLocalStorage(newValueJSON);
        }
    }, [filingCabinet, filingCabinetLocalStorage, setFilingCabinetLocalStorage]);

    const neverNullFilingCabinet = useMemo(() => filingCabinet ?? createNewCabinet(), [filingCabinet]);
    return [neverNullFilingCabinet, setFilingCabinet];
};

export default useFilingCabinetBackend;
